import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import { getStudentExaminationOverview } from '../../../../redux/features/crmSlice';
import ExamScoreChart, { SERIES } from '../../../crm/myworkspace/Dashboard/ExamScoreChart';

//SAME DECORATIVE SET THE CRO DASHBOARD POPUP USES . CHROME , NOT A DATA ENCODING :
//BLUE AND ORANGE ARE ABSENT SO A CARD CANNOT BE READ AS A CHART SERIES.
const GRADIENTS = [
  { from: '#3730a3', to: '#1e1b4b' },
  { from: '#0f766e', to: '#042f2e' },
  { from: '#9f1239', to: '#4c0519' },
  { from: '#15803d', to: '#052e16' },
  { from: '#6b21a8', to: '#2e1065' },
  { from: '#92400e', to: '#451a03' },
];
const ON_DARK = { up: '#4ade80', down: '#fca5a5' };
const TIER_ICON = ['mdi:flag-outline', 'mdi:notebook-edit-outline', 'mdi:trophy-outline'];

const tierOf = (name = '') => {
  const n = String(name).toLowerCase();
  if (n.includes('pre')) return 0;
  if (n.includes('post')) return 2;
  return 1;
};

const formatDate = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');
const marksText = (o, t) => (o === null || o === undefined ? '—' : `${o} / ${t ?? '—'}`);

//TOTAL MARKS DIFFER BETWEEN TESTS ( 20 , 15 , 40 ... ) SO EVERY COMPARISON IS A PERCENTAGE
export const percentOf = (test) =>
  test?.marks_obtained === null || test?.marks_obtained === undefined || !test?.total_marks
    ? null
    : (test.marks_obtained / test.total_marks) * 100;

const ratioPercent = (obtained, total) =>
  obtained === null || obtained === undefined || !total ? null : (obtained / total) * 100;

//DIFFICULTY BAND OF A CRITERION . ORDINAL ( basic -> advanced ) , SO IT TAKES A ONE HUE
//RAMP RATHER THAN ARBITRARY COLOURS , AND A DELIBERATELY DIFFERENT HUE FROM THE BLUE
//TEST RAMP SO A BAND CAN NEVER BE MISTAKEN FOR A TEST. THE BACKEND ALREADY RESOLVES THE
//NAME , INCLUDING "Category not added" FOR THE ROWS WHERE THE COLUMN IS STILL NULL.
const CATEGORY_TINT = { 1: '#7e9cc4', 2: '#4a6fa5', 3: '#1f3f6b' };

/**
 * the difficulty band chip for one criterion. renders muted when the criterion has no
 * category set, rather than hiding the fact.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type component
 */
const CategoryChip = ({ categoryId, categoryName }) => {
  const theme = useTheme();
  if (!categoryName) return null;
  const tint = CATEGORY_TINT[categoryId];
  return (
    <Chip
      size="small"
      label={categoryName}
      sx={{
        height: 18,
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
        '& .MuiChip-label': { px: 0.85 },
        ...(tint
          ? { color: tint, bgcolor: alpha(tint, 0.12), border: '1px solid', borderColor: alpha(tint, 0.35) }
          : {
              color: 'text.disabled',
              bgcolor: 'transparent',
              border: '1px dashed',
              borderColor: alpha(theme.palette.text.primary, 0.25),
              fontWeight: 500,
            }),
      }}
    />
  );
};

/**
 * reduce a subject to its pre assessment and its post assessment.
 * a subject that has not reached the post assessment yet falls back to its latest SCORED
 * test , flagged with isProvisional so the ui never claims a final result it does not have.
 * @author Akhil E M
 * @createdAt 11-08-2026
 * @updates 11-08-2026(Akhil EM),
 * @type function
 * @param {object} subject
 * @returns {object}
 */
export const summariseSubject = (subject) => {
  const scored = (subject?.tests || [])
    .map((t) => ({ ...t, percent: percentOf(t) }))
    .filter((t) => t.percent !== null);

  const pre = scored.find((t) => tierOf(t.student_examination_type_name) === 0) ?? scored[0] ?? null;
  const post = scored.find((t) => tierOf(t.student_examination_type_name) === 2) ?? null;
  const fallback = scored.length > 1 ? scored[scored.length - 1] : null;
  const end = post ?? (fallback && fallback !== pre ? fallback : null);

  return {
    ...subject,
    scoredCount: scored.length,
    testCount: subject?.tests?.length ?? 0,
    pre,
    end,
    isProvisional: !post && Boolean(end),
    growth: pre && end ? end.percent - pre.percent : null,
  };
};

/**
 * which AREAS moved, by diffing each subject's headline criteria at the first assessment
 * against the same criteria at the latest one. only PARENT criteria count as an "area" —
 * sub-criteria are detail that lives further down the popup. totals differ between tests
 * so every delta is in percentage points.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type function
 * @param {array} summary
 * @returns {object}
 */
export const summariseAreas = (summary) => {
  const areas = [];
  (summary || []).forEach((s) => {
    if (!s.pre || !s.end || s.pre === s.end) return;
    const start = new Map();
    (s.pre.criteria || []).forEach((p) => start.set(p.student_examination_criteria_id, p));
    (s.end.criteria || []).forEach((p) => {
      const before = start.get(p.student_examination_criteria_id);
      const now = ratioPercent(p.marks_obtained, p.total_marks);
      const was = before ? ratioPercent(before.marks_obtained, before.total_marks) : null;
      if (now === null || was === null) return;
      areas.push({ subject: s.subject_name, name: p.criteria_name, delta: now - was });
    });
  });
  return {
    total: areas.length,
    gained: areas.filter((a) => a.delta >= 0.5).sort((a, b) => b.delta - a.delta),
    lost: areas.filter((a) => a.delta <= -0.5).sort((a, b) => a.delta - b.delta),
  };
};

/**
 * a plain sentence describing what improved. falls back to subject level wording when no
 * criteria breakdown was recorded, rather than implying detail that is not there.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type function
 * @param {object} overall
 * @param {object} areaMovement
 * @returns {string}
 */
export const buildNarrative = (overall, areaMovement) => {
  const label = (a) => `${a.name} (${a.subject})`;
  const signed = (d) => `${d >= 0 ? '+' : ''}${d.toFixed(0)}%`;

  if (!overall.comparable) {
    return 'Only one test has been recorded so far, so there is nothing to compare against yet.';
  }

  const plural = overall.comparable === 1 ? 'subject' : 'subjects';
  const parts = [
    overall.improved === overall.comparable
      ? `Every one of the ${overall.comparable} comparable ${plural} improved`
      : `${overall.improved} of ${overall.comparable} ${plural} improved`,
  ];
  //"STRONGEST" WOULD BE A LIE WHEN THE BEST SUBJECT STILL WENT BACKWARDS
  if (overall.best) {
    parts.push(
      overall.best.growth > 0
        ? `with ${overall.best.subject_name} strongest at ${signed(overall.best.growth)}`
        : `with ${overall.best.subject_name} holding up best at ${signed(overall.best.growth)}`,
    );
  }
  let text = `${parts.join(', ')}.`;

  if (!areaMovement.total) {
    return `${text} Criteria level marks have not been recorded for these tests, so improvement can only be shown at subject level.`;
  }

  const top = areaMovement.gained.slice(0, 3);
  if (top.length) {
    text += ` Within the criteria recorded, ${top.map((a) => `${label(a)} ${signed(a.delta)}`).join(', ')} improved the most`;
    text += areaMovement.lost.length ? '.' : ', and no area went backwards.';
  } else {
    text += ' No criterion improved between the first and the latest assessment.';
  }
  if (areaMovement.lost.length) {
    text += ` ${areaMovement.lost.length} area${areaMovement.lost.length === 1 ? '' : 's'} went backwards: ${areaMovement.lost
      .slice(0, 3)
      .map((a) => `${label(a)} ${signed(a.delta)}`)
      .join(', ')}.`;
  }
  return text;
};

const Delta = ({ value, size = 13, onDark = false }) => {
  const theme = useTheme();
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  const colour = onDark
    ? up
      ? ON_DARK.up
      : ON_DARK.down
    : up
      ? theme.palette.success.main
      : theme.palette.error.main;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Icon icon={up ? 'mdi:trending-up' : 'mdi:trending-down'} width={size + 3} height={size + 3} color={colour} />
      <Typography sx={{ fontWeight: 700, fontSize: size, color: colour }}>
        {up ? '+' : ''}
        {value.toFixed(0)}%
      </Typography>
    </Stack>
  );
};

/* ════════════════════════════ CIRCULAR CHART ════════════════════════════ */

const RING_BOX = 240;
const RING_OUTER = 106;
//KEEPS THE CENTRE HOLE WIDE ENOUGH FOR THE GROWTH FIGURE EVEN AT EIGHT TESTS
const RING_MIN_INNER = 46;

/**
 * the test sequence pre -> exam -> post is ORDINAL , so it takes a one hue ramp rather
 * than arbitrary decorative colours. these are the same three validated steps the cro
 * dashboard popup uses : monotone lightness , adjacent dL >= 0.06 , light end clears
 * 2:1 on both paper surfaces , single hue. the anchor flips in dark mode.
 * the ramp encodes the three TIERS rather than every individual test , because an
 * eight step version of the same ramp failed the adjacent lightness gate.
 */
const TIERS = {
  light: ['#86b6ef', '#3987e5', '#1c5cab'],
  dark: ['#1c5cab', '#3987e5', '#86b6ef'],
};

/**
 * geometry for one subject's layered rings : one ring per scored test , the LATEST
 * outermost , earlier tests nested inside it. the band width adapts to the test count
 * so the middle stays free for the growth figure however long the sequence runs.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type function
 * @param {number} count
 * @returns {object}
 */
export const ringGeometry = (count) => {
  if (count <= 0) return { radii: [], stroke: 0 };
  if (count === 1) return { radii: [RING_OUTER], stroke: 13 };
  const step = Math.min(17, (RING_OUTER - RING_MIN_INNER) / (count - 1));
  return {
    radii: Array.from({ length: count }, (_, i) => RING_OUTER - i * step),
    stroke: Math.max(4, Math.min(13, step - 4)),
  };
};

/**
 * ONE compact layered chart per subject. each ring is a single test in that subject —
 * the latest on the outside, the pre assessment innermost — so a rising sequence reads
 * as rings growing outward. the middle carries the subject's overall growth.
 * NOTE : rings of differing radius turn equal percentages into unequal arc LENGTHS , so
 * the chart is read by ANGLE against the quarter spokes , and every test is repeated as
 * text with its score directly beneath the chart.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type component
 */
const SubjectRingChart = ({ subject, gradient }) => {
  const theme = useTheme();
  const tiers = theme.palette.mode === 'dark' ? TIERS.dark : TIERS.light;
  const cx = RING_BOX / 2;

  const scored = useMemo(
    () => (subject?.tests || []).map((t) => ({ ...t, percent: percentOf(t) })).filter((t) => t.percent !== null),
    [subject],
  );
  //LATEST OUTERMOST , SO GROWTH READS AS THE RINGS OPENING OUTWARD
  const rings = useMemo(() => [...scored].reverse(), [scored]);
  const { radii, stroke } = ringGeometry(rings.length);

  if (!rings.length) return null;

  const innerEdge = radii[radii.length - 1] - stroke / 2 - 2;
  const outerEdge = radii[0] + stroke / 2 + 2;
  const grew = (subject.growth ?? 0) >= 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        //WIDE ENOUGH THAT THE TEST COLUMN HOLDS "POST ASSESSMENT" BESIDE A 240px CHART
        //WITHOUT TRUNCATING , AND TWO CARDS STILL SIT SIDE BY SIDE IN THE FULLSCREEN DIALOG
        width: { xs: '100%', sm: 660 },
        maxWidth: '100%',
        borderRadius: 3.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {subject.subject_name || '—'}
          </Typography>
        </Stack>
        {subject.isProvisional && <Chip size="small" variant="outlined" label="Post pending" sx={{ height: 19, fontSize: 10 }} />}
      </Stack>

      {/* CHART ON THE LEFT , THE TEST TABLE BESIDE IT . STACKS ON NARROW SCREENS */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
        {/* THE PLOT SITS ON ITS OWN TINTED , ROUNDED SURFACE */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: RING_BOX + 16 },
            flexShrink: 0,
            borderRadius: 3,
            bgcolor: alpha(gradient.from, theme.palette.mode === 'dark' ? 0.16 : 0.06),
            display: 'flex',
            justifyContent: 'center',
            py: 1,
          }}
        >
          <svg viewBox={`0 0 ${RING_BOX} ${RING_BOX}`} width="100%" style={{ display: 'block', maxWidth: RING_BOX }}>
            {/* QUARTER SPOKES , SO TWO RINGS CAN BE COMPARED BY ANGLE */}
            {[25, 50, 75].map((stop) => {
              const rad = ((stop / 100) * 360 - 90) * (Math.PI / 180);
              return (
                <line
                  key={stop}
                  x1={cx + innerEdge * Math.cos(rad)}
                  y1={cx + innerEdge * Math.sin(rad)}
                  x2={cx + outerEdge * Math.cos(rad)}
                  y2={cx + outerEdge * Math.sin(rad)}
                  stroke={alpha(theme.palette.text.primary, 0.14)}
                  strokeWidth={1}
                />
              );
            })}

            {rings.map((t, i) => {
              const r = radii[i];
              const c = 2 * Math.PI * r;
              const colour = tiers[tierOf(t.student_examination_type_name)];
              //A ROUND CAP ADDS stroke/2 BEYOND EACH END , WHICH WOULD OVERSTATE EVERY ARC AND
              //BADLY OVERSTATE SMALL ONES ( AN 8% RING RENDERING NEAR 15% ). SHORTEN THE DASH BY
              //THE OVERHANG AND PUSH IT FORWARD BY HALF , SO THE PAINTED EXTENT IS THE TRUE VALUE.
              //ARCS TOO SHORT TO CARRY TWO CAPS FALL BACK TO A FLAT END RATHER THAN LIE.
              const extent = (t.percent / 100) * c;
              const rounded = extent > stroke;
              return (
                <g key={t.student_examination_id}>
                  <title>{`${t.student_examination_type_name}: ${t.marks_obtained}/${t.total_marks} (${t.percent.toFixed(0)}%)`}</title>
                  <circle cx={cx} cy={cx} r={r} fill="none" stroke={alpha(theme.palette.text.primary, 0.08)} strokeWidth={stroke} />
                  {t.percent > 0 && (
                    <circle
                      cx={cx}
                      cy={cx}
                      r={r}
                      fill="none"
                      stroke={colour}
                      strokeWidth={stroke}
                      strokeLinecap={rounded ? 'round' : 'butt'}
                      strokeDasharray={`${rounded ? extent - stroke : extent} ${c}`}
                      strokeDashoffset={rounded ? -stroke / 2 : 0}
                      transform={`rotate(-90 ${cx} ${cx})`}
                      style={{ transition: 'stroke-dasharray 450ms ease' }}
                    />
                  )}
                </g>
              );
            })}
          </svg>

          <Stack sx={{ position: 'absolute', inset: 0 }} alignItems="center" justifyContent="center" spacing={0}>
            {subject.growth === null ? (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: 24, lineHeight: 1.1 }}>
                  {(subject.pre?.percent ?? 0).toFixed(0)}%
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>one test</Typography>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 26,
                    lineHeight: 1.1,
                    color: grew ? 'success.main' : 'error.main',
                  }}
                >
                  {subject.growth >= 0 ? '+' : ''}
                  {subject.growth.toFixed(0)}%
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>growth</Typography>
              </>
            )}
          </Stack>
        </Box>

        {/* EVERY RING REPEATED AS TEXT , OUTERMOST FIRST */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Box sx={{ width: 9, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.4, flex: 1 }}>
              TEST
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.4, minWidth: 76, textAlign: 'right' }}>
              DATE
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.4, minWidth: 60, textAlign: 'right' }}>
              MARKS
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.4, minWidth: 38, textAlign: 'right' }}>
              %
            </Typography>
          </Stack>

          {rings.map((t, i) => (
            <Stack
              key={t.student_examination_id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 1,
                py: 0.85,
                borderRadius: 1.5,
                bgcolor: i % 2 ? 'transparent' : alpha(theme.palette.text.primary, 0.025),
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: tiers[tierOf(t.student_examination_type_name)],
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0, fontSize: 13 }} noWrap>
                {t.student_examination_type_name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 76, textAlign: 'right' }} noWrap>
                {formatDate(t.examination_date)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', minWidth: 60, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              >
                {t.marks_obtained}/{t.total_marks}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, minWidth: 38, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
              >
                {t.percent.toFixed(0)}%
              </Typography>
            </Stack>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};

/**
 * every subject's layered chart, laid out in a centred row that wraps.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type component
 */
const SubjectRingRow = ({ subjects }) => {
  const theme = useTheme();
  const tiers = theme.palette.mode === 'dark' ? TIERS.dark : TIERS.light;
  return (
    <Stack spacing={3}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="center" alignItems="flex-start" sx={{ gap: 2.5 }}>
        {subjects.map((s, i) => (
          <SubjectRingChart key={s.subject_enrollment_id} subject={s} gradient={GRADIENTS[i % GRADIENTS.length]} />
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={2.5}
        flexWrap="wrap"
        useFlexGap
        justifyContent="center"
        sx={{ pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {['Pre assessment', 'Exam', 'Post assessment'].map((label, i) => (
          <Stack key={label} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: tiers[i] }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

/* ════════════════════════════ CRITERIA ════════════════════════════ */

/**
 * one criterion as a meter, with its movement against the same criterion in the
 * previous test called out explicitly.
 * @author Akhil E M
 * @createdAt 11-08-2026
 * @updates 11-08-2026(Akhil EM),
 * @type component
 */
const CriterionRow = ({ name, obtained, total, previous, categoryId, categoryName, gradient, sub = false }) => {
  const theme = useTheme();
  const pct = ratioPercent(obtained, total);
  const prevPct = previous ? ratioPercent(previous.marks_obtained, previous.total_marks) : null;
  const delta = pct !== null && prevPct !== null ? pct - prevPct : null;

  const moved = delta !== null && Math.abs(delta) >= 0.5;
  const up = delta !== null && delta > 0;
  const tint = !moved ? null : up ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box
      sx={{
        //PADDING IS IDENTICAL WHETHER OR NOT THE ROW MOVED , SO TINTING NEVER SHIFTS A ROW.
        //SEPARATE pl / pr / ml KEYS , NEVER THE px SHORTHAND , WHICH WOULD OVERRIDE pl.
        py: 0.9,
        pl: sub ? 4 : 1,
        pr: 1,
        ml: -1,
        borderRadius: 1.5,
        //A CHANGED CRITERION IS TINTED SO IT IS FINDABLE WITHOUT READING EVERY NUMBER
        bgcolor: moved ? alpha(tint, 0.07) : 'transparent',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 0.6 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {!sub && (
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})` }} />
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: sub ? 400 : 600, color: sub ? 'text.secondary' : 'text.primary' }}
            noWrap
          >
            {name ?? '—'}
          </Typography>
          <CategoryChip categoryId={categoryId} categoryName={categoryName} />
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
          {delta !== null && (
            <Tooltip
              title={
                previous
                  ? `Previously ${previous.marks_obtained}/${previous.total_marks} (${prevPct.toFixed(0)}%)`
                  : ''
              }
              placement="top"
            >
              <Box>
                {moved ? (
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <Icon icon={up ? 'mdi:arrow-up-bold' : 'mdi:arrow-down-bold'} width="14" height="14" color={tint} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>
                      {up ? '+' : ''}
                      {delta.toFixed(0)}%
                    </Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <Icon icon="mdi:minus" width="14" height="14" color={theme.palette.text.disabled} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                      no change
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Tooltip>
          )}
          <Typography variant="body2" sx={{ fontWeight: sub ? 500 : 700, fontVariantNumeric: 'tabular-nums' }}>
            {obtained === null || obtained === undefined ? '—' : `${obtained} / ${total ?? '—'}`}
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ position: 'relative', height: sub ? 4 : 6, borderRadius: 999, bgcolor: alpha(theme.palette.text.primary, 0.07), overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${pct ?? 0}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${gradient.from}, ${gradient.to})`,
            opacity: sub ? 0.65 : 1,
            transition: 'width 400ms ease',
          }}
        />
        {/* WHERE THIS CRITERION SAT IN THE PREVIOUS TEST . THE TICK FLIPS CONTRAST
            DEPENDING ON WHETHER IT LANDS ON THE FILLED BAR OR ON THE EMPTY TRACK */}
        {prevPct !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `calc(${prevPct}% - 1px)`,
              width: 2,
              bgcolor: prevPct <= (pct ?? 0) ? alpha('#fff', 0.9) : alpha(theme.palette.text.primary, 0.55),
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const ScoreRing = ({ percent, size = 88 }) => {
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = percent === null ? 0 : (percent / 100) * circumference;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha('#ffffff', 0.18)} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#ffffff"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 450ms ease' }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>
          {percent === null ? '—' : `${percent.toFixed(0)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

/* ════════════════════════════ SUBJECT DETAIL ════════════════════════════ */

const SubjectDetail = ({ subject, gradient }) => {
  const theme = useTheme();
  const [selectedTestId, setSelectedTestId] = useState(null);

  const tests = useMemo(
    () =>
      (subject?.tests || []).map((t, i, all) => {
        const previous = i > 0 ? all[i - 1] : null;
        const p = percentOf(t);
        const pp = percentOf(previous);
        return { ...t, index: i, previous, percent: p, delta: p !== null && pp !== null ? p - pp : null };
      }),
    [subject],
  );

  useEffect(() => {
    setSelectedTestId(tests[0]?.student_examination_id ?? null);
  }, [tests]);

  const selected = tests.find((t) => t.student_examination_id === selectedTestId) || tests[0] || null;

  //FLATTEN THE PREVIOUS TEST'S CRITERIA SO EACH ROW CAN FIND ITS OWN EARLIER SCORE
  const previousCriteria = useMemo(() => {
    const map = new Map();
    (selected?.previous?.criteria || []).forEach((p) => {
      map.set(p.student_examination_criteria_id, p);
      (p.subCriteria || []).forEach((s) => map.set(s.student_examination_criteria_id, s));
    });
    return map;
  }, [selected]);

  if (!selected) {
    return (
      <Paper elevation={0} sx={{ py: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No tests recorded for {subject?.subject_name}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
      {/* ---------- TEST TIMELINE ---------- */}
      <Box sx={{ width: { xs: '100%', md: 290 }, flexShrink: 0 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 1.5 }}>
          Test timeline
        </Typography>
        <Stack>
          {tests.map((t, i) => {
            const active = t.student_examination_id === selected.student_examination_id;
            const last = i === tests.length - 1;
            return (
              <Box key={t.student_examination_id} sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
                <Stack alignItems="center" sx={{ width: 34, flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      mt: '13px',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                      boxShadow: active ? `0 0 0 4px ${alpha(gradient.from, 0.28)}` : `0 1px 3px ${alpha('#000', 0.25)}`,
                      flexShrink: 0,
                    }}
                  >
                    <Icon icon={TIER_ICON[tierOf(t.student_examination_type_name)]} width="13" height="13" />
                  </Box>
                  {!last && <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', mt: 1 }} />}
                </Stack>

                <Paper
                  elevation={0}
                  onClick={() => setSelectedTestId(t.student_examination_id)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    alignSelf: 'flex-start',
                    mb: last ? 0 : 2.5,
                    p: 1.5,
                    cursor: 'pointer',
                    borderRadius: 2.5,
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
                    border: '1px solid',
                    borderColor: active ? alpha('#fff', 0.5) : 'transparent',
                    opacity: active ? 1 : 0.82,
                    boxShadow: active ? `0 8px 22px ${alpha(gradient.from, 0.45)}` : `0 2px 8px ${alpha('#000', 0.18)}`,
                    transition: 'all 200ms ease',
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff' }} noWrap>
                      {t.student_examination_type_name}
                    </Typography>
                    <Delta value={t.delta} size={12} onDark />
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.75) }}>
                      {formatDate(t.examination_date)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                      {marksText(t.marks_obtained, t.total_marks)}
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1.25, height: 5, borderRadius: 999, bgcolor: alpha('#fff', 0.22) }}>
                    <Box sx={{ height: '100%', width: `${t.percent ?? 0}%`, borderRadius: 999, bgcolor: '#fff', transition: 'width 350ms ease' }} />
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ---------- SELECTED TEST ---------- */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 3,
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
              boxShadow: `0 10px 30px ${alpha(gradient.from, 0.35)}`,
              '&:before': {
                content: '""',
                position: 'absolute',
                top: -70,
                right: -40,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: alpha('#fff', 0.08),
              },
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ position: 'relative' }}>
              <ScoreRing percent={selected.percent} />
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                    {selected.student_examination_type_name}
                  </Typography>
                  <Chip
                    size="small"
                    label={marksText(selected.marks_obtained, selected.total_marks)}
                    sx={{ fontWeight: 700, color: '#fff', bgcolor: alpha('#fff', 0.2), border: '1px solid', borderColor: alpha('#fff', 0.35) }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.85) }}>
                    {formatDate(selected.examination_date)}
                  </Typography>
                  {selected.delta !== null && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Delta value={selected.delta} onDark />
                      <Typography variant="body2" sx={{ color: alpha('#fff', 0.85) }}>
                        vs {selected.previous?.student_examination_type_name}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                {selected.tutor_suggestions ? (
                  <Typography variant="body2" sx={{ color: '#fff', mt: 0.5 }}>
                    {selected.tutor_suggestions}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selected.previous?.hasBreakdown
                ? `${selected.previous.student_examination_type_name} vs ${selected.student_examination_type_name}`
                : 'Score by criteria'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Each criterion as a percentage of its total
            </Typography>
            <Box sx={{ mt: 2 }}>
              {selected.hasBreakdown || selected.previous?.hasBreakdown ? (
                <ExamScoreChart
                  currentTest={selected}
                  previousTest={selected.previous}
                  currentLabel={selected.student_examination_type_name}
                  previousLabel={selected.previous?.student_examination_type_name}
                />
              ) : (
                <Paper elevation={0} sx={{ py: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                  <Icon icon="mdi:chart-box-outline" width="32" height="32" color={theme.palette.text.disabled} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    No criteria breakdown recorded for this test
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>

          {/* ---------- CRITERIA DETAIL WITH MOVEMENT ---------- */}
          {selected.hasBreakdown && (
            <Box>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Criteria detail
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {previousCriteria.size
                    ? `Movement shown against ${selected.previous?.student_examination_type_name}`
                    : 'First scored test, nothing to compare against yet'}
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {selected.criteria.map((parent) => (
                  <Paper
                    key={parent.student_examination_criteria_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background: `linear-gradient(180deg, ${gradient.from}, ${gradient.to})`,
                      },
                    }}
                  >
                    <Box sx={{ pl: 1 }}>
                      <CriterionRow
                        name={parent.criteria_name}
                        obtained={parent.marks_obtained}
                        total={parent.total_marks}
                        previous={previousCriteria.get(parent.student_examination_criteria_id)}
                        categoryId={parent.category_id}
                        categoryName={parent.category_name}
                        gradient={gradient}
                      />
                      {parent.subCriteria?.map((sub) => (
                        <CriterionRow
                          key={sub.student_examination_child_id}
                          name={sub.criteria_name}
                          obtained={sub.marks_obtained}
                          total={sub.total_marks}
                          previous={previousCriteria.get(sub.student_examination_criteria_id)}
                          categoryId={sub.category_id}
                          categoryName={sub.category_name}
                          gradient={gradient}
                          sub
                        />
                      ))}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

/* ════════════════════════════ MAIN ════════════════════════════ */

const StudentImpactOverview = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [subjectTab, setSubjectTab] = useState(0);
  const dispatch = useDispatch();
  const theme = useTheme();

  const { student, subjects, loading } = useSelector((state) => state.crm.studentExaminationOverview);

  useEffect(() => {
    if (open && row?.admn_no) dispatch(getStudentExaminationOverview({ admn_no: row.admn_no }));
  }, [open, row?.admn_no, dispatch]);

  const summary = useMemo(() => (subjects || []).map(summariseSubject), [subjects]);

  useEffect(() => {
    setSubjectTab(0);
  }, [summary.length]);

  const overall = useMemo(() => {
    const withGrowth = summary.filter((s) => s.growth !== null);
    const withPre = summary.filter((s) => s.pre);
    const avg = (list, pick) => (list.length ? list.reduce((a, s) => a + pick(s), 0) / list.length : null);
    const best = withGrowth.length ? withGrowth.reduce((a, s) => (s.growth > a.growth ? s : a)) : null;
    const weakest = withGrowth.length ? withGrowth.reduce((a, s) => (s.growth < a.growth ? s : a)) : null;
    return {
      subjectCount: summary.length,
      testCount: summary.reduce((a, s) => a + s.testCount, 0),
      avgPre: avg(withPre, (s) => s.pre.percent),
      avgEnd: avg(summary.filter((s) => s.end), (s) => s.end.percent),
      avgGrowth: avg(withGrowth, (s) => s.growth),
      improved: withGrowth.filter((s) => s.growth > 0).length,
      comparable: withGrowth.length,
      completed: summary.filter((s) => s.end && !s.isProvisional).length,
      best,
      weakest,
    };
  }, [summary]);

  const areaMovement = useMemo(() => summariseAreas(summary), [summary]);
  const narrative = useMemo(() => buildNarrative(overall, areaMovement), [overall, areaMovement]);

  const activeSubject = summary[subjectTab] || summary[0] || null;
  const activeGradient = GRADIENTS[(subjectTab >= 0 ? subjectTab : 0) % GRADIENTS.length];
  const hero = GRADIENTS[1];

  const journeyGrew = (overall.avgGrowth ?? 0) >= 0;

  return (
    <GlobalDialog
      fullScreen
      open={open}
      setOpen={setOpen}
      closeButton
      title={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(135deg, ${hero.from}, ${hero.to})` }}>
            <Icon icon="mdi:chart-box-multiple-outline" width="21" height="21" />
          </Box>
          <Stack spacing={0.1}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Overall Impact Report
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {[student?.student_name ?? row?.student_name, row?.admn_no && `Adm ${row.admn_no}`, row?.class_name && `Class ${row.class_name}`]
                .filter(Boolean)
                .join('  ·  ')}
            </Typography>
          </Stack>
        </Stack>
      }
      popupButton={
        <Button size="small" startIcon={<Icon icon="mdi:chart-box-multiple-outline" width="18" height="18" />} onClick={() => setOpen(true)}>
          View
        </Button>
      }
      content={
        loading ? (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Skeleton variant="rounded" height={200} />
            <Skeleton variant="rounded" height={300} />
          </Stack>
        ) : !summary.length ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Icon icon="mdi:clipboard-text-off-outline" width="46" height="46" color={theme.palette.text.disabled} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              No subjects with examination data for this student
            </Typography>
          </Box>
        ) : (
          <Stack spacing={4} sx={{ py: 1 }}>
            {/* ══════════ TOP CARD ══════════ */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                color: '#fff',
                position: 'relative',
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${hero.from} 0%, ${hero.to} 100%)`,
                boxShadow: `0 14px 40px ${alpha(hero.from, 0.4)}`,
              }}
            >
              <Box sx={{ position: 'absolute', top: -90, right: -60, width: 280, height: 280, borderRadius: '50%', background: alpha('#fff', 0.07) }} />
              <Box sx={{ position: 'absolute', bottom: -120, right: 140, width: 220, height: 220, borderRadius: '50%', background: alpha('#fff', 0.05) }} />

              <Stack direction={{ xs: 'column', md: 'row' }} sx={{ position: 'relative' }}>
                {/* HERO FIGURE : THE ONE NUMBER THIS POPUP LEADS WITH */}
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    p: { xs: 3, md: 4 },
                    minWidth: { md: 260 },
                    borderRight: { md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: { xs: alpha('#fff', 0.18), md: alpha('#fff', 0.18) },
                  }}
                >
                  <Typography variant="overline" sx={{ color: alpha('#fff', 0.8), letterSpacing: 1.2 }}>
                    Overall growth
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Icon icon={journeyGrew ? 'mdi:trending-up' : 'mdi:trending-down'} width="40" height="40" color={journeyGrew ? ON_DARK.up : ON_DARK.down} />
                    <Typography sx={{ fontWeight: 800, fontSize: 52, lineHeight: 1, color: journeyGrew ? ON_DARK.up : ON_DARK.down }}>
                      {overall.avgGrowth === null ? '—' : `${overall.avgGrowth >= 0 ? '+' : ''}${overall.avgGrowth.toFixed(0)}%`}
                    </Typography>
                  </Stack>

                  {/* THE JOURNEY : AVERAGE PRE -> AVERAGE POST */}
                  <Box sx={{ width: '100%', maxWidth: 210, mt: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.8) }}>
                        Pre {overall.avgPre === null ? '—' : `${overall.avgPre.toFixed(0)}%`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                        Post {overall.avgEnd === null ? '—' : `${overall.avgEnd.toFixed(0)}%`}
                      </Typography>
                    </Stack>
                    <Box sx={{ position: 'relative', height: 8, borderRadius: 999, bgcolor: alpha('#fff', 0.2) }}>
                      <Box sx={{ position: 'absolute', inset: 0, width: `${overall.avgEnd ?? 0}%`, borderRadius: 999, bgcolor: '#fff', transition: 'width 450ms ease' }} />
                      <Box sx={{ position: 'absolute', top: -3, left: `calc(${overall.avgPre ?? 0}% - 1px)`, width: 2, height: 14, bgcolor: alpha('#000', 0.4), borderRadius: 1 }} />
                    </Box>
                  </Box>
                </Stack>

                {/* DETAIL GRID */}
                <Box sx={{ flex: 1, p: { xs: 3, md: 4 } }}>
                  <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 3 }}>
                    {[
                      { label: 'Subjects', value: overall.subjectCount, icon: 'mdi:book-open-variant' },
                      { label: 'Tests recorded', value: overall.testCount, icon: 'mdi:clipboard-text-outline' },
                      { label: 'Subjects improved', value: `${overall.improved} of ${overall.comparable}`, icon: 'mdi:arrow-up-bold-circle-outline' },
                      { label: 'Post assessment done', value: `${overall.completed} of ${overall.subjectCount}`, icon: 'mdi:trophy-outline' },
                    ].map((s) => (
                      <Stack key={s.label} direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 175 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: alpha('#fff', 0.14), border: '1px solid', borderColor: alpha('#fff', 0.22), flexShrink: 0 }}>
                          <Icon icon={s.icon} width="19" height="19" color="#fff" />
                        </Box>
                        <Stack spacing={0}>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.75) }}>
                            {s.label}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: 19, lineHeight: 1.2 }}>{s.value}</Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>

                  {(overall.best || overall.weakest) && (
                    <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1, mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: alpha('#fff', 0.18) }}>
                      {overall.best && (
                        <Chip
                          size="small"
                          icon={<Icon icon="mdi:star-outline" width="15" height="15" color="#fff" />}
                          label={`Strongest growth: ${overall.best.subject_name} ${overall.best.growth >= 0 ? '+' : ''}${overall.best.growth.toFixed(0)}%`}
                          sx={{ color: '#fff', bgcolor: alpha('#fff', 0.14), border: '1px solid', borderColor: alpha('#fff', 0.25) }}
                        />
                      )}
                      {overall.weakest && overall.weakest !== overall.best && (
                        <Chip
                          size="small"
                          //A SUBJECT THAT STILL GREW IS NOT A CONCERN , IT IS SIMPLY THE SMALLEST GAIN
                          icon={
                            <Icon
                              icon={overall.weakest.growth > 0 ? 'mdi:arrow-bottom-right' : 'mdi:alert-circle-outline'}
                              width="15"
                              height="15"
                              color="#fff"
                            />
                          }
                          label={`${overall.weakest.growth > 0 ? 'Smallest gain' : 'Needs attention'}: ${overall.weakest.subject_name} ${
                            overall.weakest.growth >= 0 ? '+' : ''
                          }${overall.weakest.growth.toFixed(0)}%`}
                          sx={{ color: '#fff', bgcolor: alpha('#fff', 0.14), border: '1px solid', borderColor: alpha('#fff', 0.25) }}
                        />
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Paper>

            {/* ══════════ WHAT ACTUALLY IMPROVED ══════════ */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '4px solid',
                borderLeftColor: journeyGrew ? 'success.main' : 'error.main',
              }}
            >
              <Stack direction="row" spacing={1.5}>
                <Icon
                  icon="mdi:lightbulb-on-outline"
                  width="22"
                  height="22"
                  color={journeyGrew ? theme.palette.success.main : theme.palette.error.main}
                />
                <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    What improved
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {narrative}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* ══════════ CIRCULAR : PRE -> POST PER SUBJECT ══════════ */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Pre to Post — all subjects
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                One chart per subject. Each ring is a single test — the latest on the outside, the pre assessment innermost — so a
                rising sequence reads as the rings opening outward. Read by angle against the quarter spokes.
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  //CLIPS THE TOP BAR TO THE ROUNDED CORNERS
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    //THE WHOLE SECTION CARRIES THE MULTI HUE SWEEP THAT USED TO BE A SOLID TOP BAR ,
                    //DROPPED TO A LOW ALPHA WASH . NO HARD EDGE ANYWHERE , AND THE CARDS STAY ON
                    //background.paper SO THEY LIFT OFF IT . CHROME ONLY — IT CARRIES NO DATA.
                    background: `linear-gradient(90deg, ${GRADIENTS.map((g) =>
                      alpha(g.from, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                    ).join(', ')})`,
                  }}
                >
                  <SubjectRingRow subjects={summary.filter((s) => s.pre)} />
                </Box>
              </Paper>
            </Box>

            {/* ══════════ PER SUBJECT ANALYTICS ══════════ */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Subject analytics
              </Typography>
              <Tabs
                value={subjectTab}
                onChange={(e, v) => setSubjectTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                {summary.map((s, i) => (
                  <Tab
                    key={s.subject_enrollment_id}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: `linear-gradient(135deg, ${GRADIENTS[i % GRADIENTS.length].from}, ${GRADIENTS[i % GRADIENTS.length].to})` }} />
                        <span>{s.subject_name || '—'}</span>
                        {s.growth !== null && <Delta value={s.growth} size={11} />}
                      </Stack>
                    }
                  />
                ))}
              </Tabs>

              {activeSubject && <SubjectDetail subject={activeSubject} gradient={activeGradient} />}
            </Box>
          </Stack>
        )
      }
    />
  );
};

export default StudentImpactOverview;
